import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { Grid } from "@mui/material";
import { BadgeBox } from "@icoach/components";
import { Box, Alert, Chip, IconButton, Typography, Icons } from "@curves/ui-base";
import { TimeLine } from "@curves/ui-shared";
import useMembershipApi from "@apis/useMembershipApi";
import { membershipItemReasonsFilterTypeMap } from "@icoach/documents/membership/staticData";
import useMessageDialog from "@util/hook/useMessageDialog";
import { ActivityRecordCard } from "@shared/components";
import { adaptMembershipChange, TimelineRecordItem } from "@icoach/memberDetail/components/memberChangeRecord";
import { displayDateText } from "@util/moment";
import { isEmpty, isFunction } from "@util/utils";
import { useSnackbar } from "notistack";

export const MembershipRecordUI = props => {
	const { className, memberID, membershipID, data, refresh } = props;
	const { deleteMemberMembershipManualGiftItemApi } = useMembershipApi();
	const { enqueueSnackbar } = useSnackbar();
	const setMessageDialog = useMessageDialog();
	const doDeleteMemberMembershipManualGiftItemApi = async (memberID, membershipID, itemID, onClose) => {
		let resp = await deleteMemberMembershipManualGiftItemApi(memberID, membershipID, itemID);
		if (resp) {
			resp && enqueueSnackbar("已刪除", { variant: "error" });
			isFunction(refresh) && refresh();
			isFunction(onClose) && onClose();
		}
	};

	const handleDeleteRecord = itemID => {
		setMessageDialog({
			open: true,
			title: "確認刪除會籍紀錄",
			msg: "送出後將無法恢復，請問確定要刪除嗎?",
			onAsyncConfirm: (_, onClose) => doDeleteMemberMembershipManualGiftItemApi(memberID, membershipID, itemID, onClose),
		});
	};

	if (isEmpty(data))
		return (
			<Alert color="info" severity="info" variant="filled" className="my-3">
				尚無參考資料
			</Alert>
		);

	const ChangeRecordExtraSection = ({ sourceData }) => {
		const { suspensionDate, recoveryDate, itemProcessMessage } = sourceData || {};

		if (isEmpty(suspensionDate) && isEmpty(recoveryDate) && isEmpty(itemProcessMessage)) return null;

		return (
			<Box className="mt-3">
				{(suspensionDate || recoveryDate) && (
					<Grid container spacing={3}>
						{suspensionDate && (
							<Grid item xs={6}>
								<BadgeBox title="暫停扣款日" content={displayDateText(suspensionDate)} color="error" />
							</Grid>
						)}
						{recoveryDate && (
							<Grid item xs={6}>
								<BadgeBox title="恢復扣款日" content={displayDateText(recoveryDate)} color="error" />
							</Grid>
						)}
					</Grid>
				)}
				{isEmpty(suspensionDate) && !isEmpty(itemProcessMessage) ? (
					<Grid container spacing={3}>
						<Grid item xs={12}>
							<Chip label={itemProcessMessage} color={"secondary"}></Chip>
						</Grid>
					</Grid>
				) : null}
			</Box>
		);
	};

	return (
		<Box className={clsx("membership-record-content", className)}>
			<TimeLine as="ul">
				{data.map((item, index) => {
					/* item.itemID 是 membershipItemID 會有重複疑慮，至此改寫 */
					const key = `${item.itemID}_${item.rangeType}_${index}`;
					const itemData = adaptMembershipChange(item);
					return (
						<TimeLine.Item key={key} as="li">
							<TimeLine.ItemContent>
								<TimelineRecordItem className="membership-record-item" metainfo={itemData.timelineMetainfo}>
									<ActivityRecordCard status={itemData.type}>
										{itemData.canDelete && (
											<ActivityRecordCard.Actions>
												<IconButton onClick={() => handleDeleteRecord(itemData.id)}>
													<Icons.Clock />
												</IconButton>
											</ActivityRecordCard.Actions>
										)}
										{itemData.pdfUrl && <ActivityRecordCard.PdfAttachment pdfUrl={itemData.pdfUrl} />}
										<ActivityRecordCard.Content style={isEmpty(itemData.descList) ? { display: "flex", alignItems: "center" } : undefined}>
											<Box className="activity-record-card__main">
												<Typography variant="h6" className="title">
													{itemData.title}
												</Typography>
												{!isEmpty(itemData.descList) &&
													itemData.descList.map((desc, descIndex) => (
														<Typography key={descIndex} variant="body2" className="mb-1">
															{desc.label ? `${desc.label}：${desc.text}` : desc.text}
														</Typography>
													))}
											</Box>
											<Box className="activity-record-card__extra">
												<ChangeRecordExtraSection sourceData={item} />
											</Box>
										</ActivityRecordCard.Content>
									</ActivityRecordCard>
								</TimelineRecordItem>
							</TimeLine.ItemContent>
						</TimeLine.Item>
					);
				})}
			</TimeLine>
		</Box>
	);
};

const MemberDetailMembershipContentRecord = props => {
	const { className, memberID, membershipID, refresh } = props;
	const { getMembershipItemReasonsApi } = useMembershipApi();
	const [data, setData] = useState({});

	const doMembershipItemReasonsApi = async (memberID, membershipID) => {
		const params = { filter: membershipItemReasonsFilterTypeMap.all };
		let res = await getMembershipItemReasonsApi(memberID, membershipID, params);
		if (res) {
			setData(res);
		}
	};

	const handleSubmitSuccessRefresh = () => {
		doMembershipItemReasonsApi(memberID, membershipID);
		refresh && refresh();
	};

	useEffect(
		() => {
			if (memberID && membershipID) doMembershipItemReasonsApi(memberID, membershipID);
		},
		// eslint-disable-next-line
		[memberID, membershipID],
	);

	return <MembershipRecordUI className={className} memberID={memberID} membershipID={membershipID} data={data} refresh={handleSubmitSuccessRefresh} />;
};

export default MemberDetailMembershipContentRecord;
